import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../types';
import { formatReadingTime } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

interface RelatedArticlesProps {
  articles: Article[];
}

/** «مقالات مرتبط»: کارت‌های بزرگ در دسکتاپ، ردیف‌های فشرده در موبایل */
const RelatedArticles: React.FC<RelatedArticlesProps> = ({ articles }) => {
  const { relatedTitle, relatedMoreLabel } = useBlogSettings();
  if (!articles.length) return null;
  return (
    <section aria-labelledby="related-heading" className="flex flex-col gap-[14px] border-t border-[#e9e8e5] pt-4 lg:gap-6 lg:pt-14">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="hidden h-[10px] w-[10px] rounded-full bg-[#331751] lg:block" aria-hidden />
          <h2 id="related-heading" className="text-[18px] font-semibold leading-[30px] text-[#331751] lg:text-[28px] lg:leading-[44px] lg:text-[#1a1c1a]">
            {relatedTitle}
          </h2>
        </div>
        <Link to="/articles" className="inline-flex items-center gap-[2px] text-[12px] font-semibold leading-5 text-[#4a2e68] hover:text-[#392352] lg:gap-1 lg:text-[14px] lg:font-medium lg:leading-6 lg:text-[#331751]">
          {relatedMoreLabel}
          <BlogIcon name="chevron-left-xxs" width={3.333} height={6.667} className="lg:hidden" />
          <BlogIcon name="chevron-all-articles" width={10.667} height={10.667} className="hidden rotate-180 lg:inline-block" />
        </Link>
      </div>

      {/* دسکتاپ: طبق طرح دو کارت بزرگ کنار هم */}
      <div className="hidden items-start gap-6 lg:grid lg:grid-cols-2">
        {articles.slice(0, 2).map((article) => {
          const summary = article.cardSummary || article.excerpt;
          const href = `/articles/${article.slug}`;
          return (
            <article key={article.id} className="flex flex-col overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-[#fdfbf7] transition-shadow hover:shadow-[0px_6px_18px_0px_rgba(30,32,34,0.08)]">
              <Link to={href} className="block aspect-[16/9] w-full overflow-hidden rounded-t-[11px] bg-[#efeeeb]" aria-label={article.title}>
                {article.featuredImage && <img src={article.featuredImage} alt={article.featuredImageAlt || article.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />}
              </Link>
              <div className="flex flex-1 flex-col justify-between p-6">
                <div className="flex flex-col items-start gap-2">
                  {article.category?.name && <span className="rounded-full bg-[#f4f1ea] px-2 py-[2px] text-[12px] font-medium leading-5 text-[#5b5f66]">{article.category.name}</span>}
                  <h3 className="blog-clamp-2 text-[18px] font-semibold leading-[24.75px] text-[#1a1c1a]">
                    <Link to={href} className="hover:text-[#331751]">
                      {article.title}
                    </Link>
                  </h3>
                  {summary && <p className="blog-clamp-2 min-h-[45.5px] text-[14px] leading-[22.75px] text-[#5b5f66]">{summary}</p>}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#e9e8e5] pt-2 text-[12px] font-medium leading-5">
                  <span className="text-[#5b5f66]">{formatReadingTime(article.readingTime)}</span>
                  <Link to={href} className="inline-flex items-center gap-1 text-[#331751] hover:text-[#392352]">
                    مطالعه مقاله
                    <BlogIcon name="arrow-read-article" width={9.333} height={9.333} className="rotate-180" />
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* موبایل */}
      <div className="flex flex-col gap-3 lg:hidden">
        {articles.map((article) => (
          <Link key={article.id} to={`/articles/${article.slug}`} className="flex items-start gap-3 rounded-[12px] border border-[#e9e8e5] bg-[#fdfbf7] p-[10px]">
            <span className="block h-20 w-24 shrink-0 overflow-hidden rounded-[8px] bg-[#efeeeb]">
              {article.featuredImage && <img src={article.featuredImage} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col justify-between gap-1 self-stretch py-[2px]">
              {article.category?.name && <span className="text-[12px] font-medium leading-5 text-[#6f518e]">{article.category.name}</span>}
              <span className="blog-clamp-2 text-[18px] font-semibold leading-[22.5px] text-[#1a1c1a]">{article.title}</span>
              <span className="inline-flex items-center gap-1 text-[12px] font-medium leading-5 text-[#5b5f66]">
                <BlogIcon name="clock-muted-sm" width={11.667} height={11.667} />
                {formatReadingTime(article.readingTime, '')}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;
